/**
 * BulkIndicatorImport — Phase 3.41
 *
 * One modal, two paths:
 *   1. CSV template — strict columns (code, name, unit, type, baseline,
 *      target, frequency, theme, region, disaggregations, description).
 *   2. AI Suggest from any sheet — upload arbitrary data, Claude
 *      proposes indicator definitions you can review/edit.
 *
 * Both paths funnel into the same preview + commit step so the UX
 * surfaces once: review every row, edit inline, tick/untick include,
 * confirm.
 */
import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, FileDown, Sparkles, AlertCircle, CheckCircle2, Trash2, Plus } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import api, { API as API_URL } from "../../lib/api";

const TYPE_OPTIONS = ["output", "outcome", "impact", "activity", "input"];

export default function BulkIndicatorImport({ pid, open, onClose, onImported }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("pick");        // pick | preview
  const [source, setSource] = useState(null);      // "csv" | "ai"
  const [loading, setLoading] = useState(false);
  const [deepening, setDeepening] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, valid: 0, invalid: 0 });
  const [aiMeta, setAiMeta] = useState({ deep: false, sample_size: 0, total_rows: 0, can_deepen: false });
  const [lastAiFile, setLastAiFile] = useState(null); // retained so deep re-analysis can re-ship the same file
  const csvInputRef = useRef(null);
  const aiInputRef = useRef(null);

  const reset = () => {
    setStep("pick"); setSource(null); setRows([]); setSummary({ total: 0, valid: 0, invalid: 0 });
    setAiMeta({ deep: false, sample_size: 0, total_rows: 0, can_deepen: false });
    setLastAiFile(null);
  };
  const close = () => { reset(); onClose(); };

  const uploadFile = async (file, mode, { deep = false } = {}) => {
    if (deep) setDeepening(true); else setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const baseUrl = mode === "ai"
        ? `${API_URL}/programs/${pid}/indicators/ai-suggest`
        : `${API_URL}/programs/${pid}/indicators/csv-preview`;
      const url = mode === "ai" && deep ? `${baseUrl}?deep=true` : baseUrl;
      const res = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRows(res.data.rows || []);
      setSummary(res.data.summary || { total: 0, valid: 0, invalid: 0 });
      setSource(mode);
      if (mode === "ai") {
        setAiMeta({
          deep: !!res.data.deep,
          sample_size: res.data.sample_size || 0,
          total_rows: res.data.total_rows || 0,
          can_deepen: !!res.data.can_deepen,
        });
        // Cache the file so the deep-pass button has something to re-ship.
        setLastAiFile(file);
        if (deep) toast.success(`Deep sweep complete — analysed ${res.data.sample_size} of ${res.data.total_rows} rows`);
      }
      setStep("preview");
      if ((res.data.rows || []).length === 0) {
        toast.info("No indicator rows detected.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
      setDeepening(false);
      if (csvInputRef.current) csvInputRef.current.value = "";
      if (aiInputRef.current) aiInputRef.current.value = "";
    }
  };

  const downloadTemplate = () => {
    // Direct hit — auth cookie travels with the request, returns CSV.
    const a = document.createElement("a");
    a.href = `${API_URL}/indicators/csv-template`;
    a.download = "indicator-template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch, parsed: { ...next[idx].parsed, ...(patch.parsed || {}) } };
      return next;
    });
  };

  const commit = async () => {
    setLoading(true);
    try {
      // Phase 3.30 — Unified ingestion. When the rows came from the AI
      // "suggest from sheet" path and we still hold the original file,
      // POST to /ai-ingest which creates indicators AND ingests rows in
      // one shot, then redirects to the dashboard. Otherwise (CSV
      // template path or AI without a cached file) keep the legacy
      // commit-only behaviour.
      if (source === "ai" && lastAiFile) {
        const fd = new FormData();
        fd.append("file", lastAiFile);
        fd.append("rows_json", JSON.stringify({ rows }));
        const res = await api.post(
          `/programs/${pid}/indicators/ai-ingest`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        const ins = res.data.inserted || 0;
        const ing = res.data.rows_ingested || 0;
        const pins = res.data.auto_pin_ids || [];
        // Auto-pin the first 4 numeric indicators to the dashboard.
        if (pins.length) {
          try {
            const raw = localStorage.getItem("ae-dashboard-pinned-v1");
            const existing = raw ? JSON.parse(raw) : [];
            const merged = [...pins, ...existing.filter((id) => !pins.includes(id))].slice(0, 4);
            localStorage.setItem("ae-dashboard-pinned-v1", JSON.stringify(merged));
          } catch { /* ignore */ }
        }
        toast.success(`Imported ${ins} indicators + ingested ${ing} rows. Visualizing now…`);
        onImported && onImported(ins);
        close();
        navigate("/");
        return;
      }
      // Fallback — legacy commit-only path
      const res = await api.post(`/programs/${pid}/indicators/csv-commit`, { rows });
      toast.success(`Imported ${res.data.inserted} indicator${res.data.inserted === 1 ? "" : "s"}`);
      if ((res.data.skipped || []).length > 0) {
        toast.info(`${res.data.skipped.length} row${res.data.skipped.length === 1 ? "" : "s"} skipped (already exists or invalid).`);
      }
      onImported && onImported(res.data.inserted);
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const includedCount = rows.filter((r) => r.include && r.status === "valid").length;
  const allValidIncluded = rows.length > 0 && rows.every((r) => r.status !== "valid" || r.include);
  const toggleAll = () => {
    setRows((prev) => prev.map((r) => ({
      ...r,
      include: r.status === "valid" ? !allValidIncluded : false,
    })));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="bulk-indicator-import">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-emerald-600" /> Bulk import indicators
          </DialogTitle>
          <DialogDescription>
            Add many indicators at once — upload a template, or let the AI infer them from any spreadsheet.
          </DialogDescription>
        </DialogHeader>

        {step === "pick" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4" data-testid="bulk-import-pick">
            <div className="border border-slate-200 rounded-md p-5 flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <Upload className="w-4 h-4 text-slate-600" />
                <h3 className="font-heading text-base font-semibold">CSV template</h3>
              </div>
              <p className="text-xs text-slate-500 mb-3 flex-1">
                Use our structured template with columns for code, name, unit, type, baseline, target, theme, region, disaggregations and description.
              </p>
              <button onClick={downloadTemplate} className="text-xs text-emerald-700 underline mb-3 text-left" data-testid="download-csv-template">
                <FileDown className="w-3 h-3 inline mr-1" /> Download template
              </button>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "csv")}
                data-testid="csv-file-input"
              />
              <Button
                onClick={() => csvInputRef.current?.click()}
                disabled={loading}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="upload-csv-btn"
              >
                {loading && source !== "ai" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                Upload filled template
              </Button>
            </div>

            <div className="border border-emerald-200 bg-emerald-50/30 rounded-md p-5 flex flex-col" data-testid="ai-suggest-card">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                <h3 className="font-heading text-base font-semibold">AI suggest from any sheet</h3>
                <span className="text-[10px] uppercase tracking-wider bg-emerald-500 text-slate-900 px-1.5 py-0.5 rounded-sm">New</span>
              </div>
              <p className="text-xs text-slate-600 mb-3 flex-1">
                Upload any spreadsheet — even messy field data. AI proposes indicator definitions (code, name, unit, type, disaggregations) you can review and edit before committing.
              </p>
              <input
                ref={aiInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "ai")}
                data-testid="ai-file-input"
              />
              <Button
                onClick={() => aiInputRef.current?.click()}
                disabled={loading}
                className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium"
                data-testid="upload-ai-btn"
              >
                {loading && source === "ai" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                Upload sheet for AI
              </Button>
              <p className="text-[10px] text-slate-500 mt-2">
                Powered by Claude. Your file is processed but not stored.
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-3 space-y-3" data-testid="bulk-import-preview">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="bg-slate-900 text-white text-xs px-2 py-1 rounded-sm font-mono" data-testid="preview-total">
                {summary.total} row{summary.total === 1 ? "" : "s"}
              </span>
              <span className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs px-2 py-1 rounded-sm font-mono">
                {summary.valid} valid
              </span>
              {summary.invalid > 0 && (
                <span className="bg-rose-50 border border-rose-200 text-rose-700 text-xs px-2 py-1 rounded-sm font-mono">
                  {summary.invalid} with errors
                </span>
              )}
              {source === "ai" && (
                <span className="ml-2 text-xs text-emerald-700 inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  {aiMeta.deep
                    ? `AI deep sweep — ${aiMeta.sample_size} of ${aiMeta.total_rows} rows analysed`
                    : aiMeta.total_rows > 0
                      ? `AI proposed from ${aiMeta.sample_size} of ${aiMeta.total_rows} rows`
                      : "AI proposed — review and edit before importing"}
                </span>
              )}
              {source === "ai" && aiMeta.can_deepen && lastAiFile && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => uploadFile(lastAiFile, "ai", { deep: true })}
                  disabled={deepening}
                  className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  data-testid="ai-deepen-btn"
                  title={`Send up to 50 rows for a deeper analysis (currently analysed ${aiMeta.sample_size} of ${aiMeta.total_rows})`}
                >
                  {deepening ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  Re-analyze with full sheet
                </Button>
              )}
              <button onClick={reset} className="ml-auto text-xs text-slate-500 underline" data-testid="preview-reset">
                Pick another file
              </button>
            </div>

            <div className="border border-slate-200 rounded-md max-h-[55vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-10">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider text-slate-500 w-8">
                      <label className="inline-flex items-center cursor-pointer" title={allValidIncluded ? "Deselect all" : "Select all"}>
                        <input
                          type="checkbox"
                          checked={allValidIncluded}
                          onChange={toggleAll}
                          className="accent-emerald-500"
                          data-testid="bulk-import-select-all"
                        />
                      </label>
                    </th>
                    <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider text-slate-500">Code</th>
                    <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider text-slate-500">Name</th>
                    <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider text-slate-500">Unit</th>
                    <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider text-slate-500">Type</th>
                    <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider text-slate-500">Theme</th>
                    <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider text-slate-500">Baseline / Target</th>
                    <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider text-slate-500">Disagg</th>
                    <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider text-slate-500 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={r.row_num} className={`border-b border-slate-100 ${r.status === "error" ? "bg-rose-50/30" : ""}`} data-testid={`preview-row-${idx}`}>
                      <td className="px-2 py-2 align-top">
                        <input
                          type="checkbox"
                          checked={r.include}
                          disabled={r.status === "error"}
                          onChange={(e) => updateRow(idx, { include: e.target.checked })}
                          className="accent-emerald-500"
                          data-testid={`include-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <Input value={r.parsed.code} onChange={(e) => updateRow(idx, { parsed: { code: e.target.value } })} className="text-xs h-8 font-mono" data-testid={`row-code-${idx}`} />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <Input value={r.parsed.name} onChange={(e) => updateRow(idx, { parsed: { name: e.target.value } })} className="text-xs h-8" data-testid={`row-name-${idx}`} />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <Input value={r.parsed.unit} onChange={(e) => updateRow(idx, { parsed: { unit: e.target.value } })} className="text-xs h-8 w-20" />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <select value={r.parsed.type} onChange={(e) => updateRow(idx, { parsed: { type: e.target.value } })} className="text-xs h-8 border border-slate-200 rounded-sm px-2">
                          {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-2 align-top">
                        <Input value={r.parsed.theme} onChange={(e) => updateRow(idx, { parsed: { theme: e.target.value } })} className="text-xs h-8 w-24" />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <div className="flex gap-1">
                          <Input type="number" placeholder="–" value={r.parsed.baseline ?? ""} onChange={(e) => updateRow(idx, { parsed: { baseline: e.target.value === "" ? null : parseFloat(e.target.value) } })} className="text-xs h-8 w-16" />
                          <Input type="number" placeholder="target" value={r.parsed.target ?? ""} onChange={(e) => updateRow(idx, { parsed: { target: e.target.value === "" ? null : parseFloat(e.target.value) } })} className="text-xs h-8 w-16" />
                        </div>
                      </td>
                      <td className="px-2 py-2 align-top">
                        <Input
                          value={(r.parsed.disaggregations || []).join(", ")}
                          onChange={(e) => updateRow(idx, { parsed: { disaggregations: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) } })}
                          placeholder="gender, age"
                          className="text-xs h-8 w-28"
                        />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <button onClick={() => setRows((prev) => prev.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-rose-600" data-testid={`remove-row-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.some((r) => r.status === "error") && (
                <div className="px-3 py-2 bg-rose-50 border-t border-rose-200 text-xs text-rose-700">
                  {rows.filter((r) => r.status === "error").map((r) => (
                    <p key={r.row_num} className="flex items-center gap-1.5">
                      <AlertCircle className="w-3 h-3" /> Row {r.row_num}: {r.errors.join("; ")}
                    </p>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-500">
                <CheckCircle2 className="w-3 h-3 inline mr-1 text-emerald-500" />
                {includedCount} indicator{includedCount === 1 ? "" : "s"} will be imported
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={close} disabled={loading}>Cancel</Button>
                <Button
                  onClick={commit}
                  disabled={loading || includedCount === 0}
                  className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium"
                  data-testid="commit-import-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                  {source === "ai" && lastAiFile
                    ? `Confirm & Visualize (${includedCount})`
                    : `Import ${includedCount} indicator${includedCount === 1 ? "" : "s"}`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
